<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Dropdown as VDropdown } from 'floating-vue'
import { useToast, TYPE } from 'vue-toastification'
import { find } from 'lodash'
import { cva } from 'class-variance-authority'
import useUtils from '@/store/utils'
import useExtensions from '@/queries/extensions'
import ExtensionInstaller from '@/components/extensions/ExtensionInstaller.vue'
import type IntegrationResource from '@/services/api/transformers/IntegrationResource'
import { PhGear } from '@phosphor-icons/vue'
import { useModalTriggers } from '@/composables/modals'
import TrinityNavIconLink from '@/layouts/components/TrinityNavIconLink.vue'
import useTithelyNavSettings from '@/composables/nav/trinitySettings'

const utils = useUtils()
const { filteredNav } = useTithelyNavSettings()
const nav = filteredNav(`church`)
const toast = useToast()
const { handleModalAction } = useModalTriggers()

const extraDropdown = ref(null)
const open = ref(false)

const dropdownProps = computed(() => ({
    triggers: [`click`],
    placement: `bottom-end`,
    distance: 9,
    theme: `main-nav-settings`,
    popperClass: `horizon-ui`,
    autoHide: true,
    positioningDisabled: utils.getMaxWidth(`md`),
    strategy: `fixed`
}))

const extensionInstallModal = ref<InstanceType<typeof ExtensionInstaller>>()

const { data: extensions } = useExtensions()
const servicePlan = computed<IntegrationResource | undefined>(() =>
    find(extensions.value, { name: 'service_plan_integration' })
)

const buttonClasses = cva(
    `flex size-10 items-center justify-center rounded-full !p-0 transition-all duration-200 ease-in-out`,
    {
        variants: {
            open: {
                true: `rotate-45 bg-primary text-white`,
                false: `text-primary hover:bg-secondary-200`
            }
        }
    }
)

const action = (hide: () => void, action?: string) => {
    hide()

    if (action) {
        switch (action) {
            case `songLibrary`:
                if (servicePlan.value) {
                    extensionInstallModal.value?.open(servicePlan.value)
                } else {
                    toast(`Extension not found`, { type: TYPE.ERROR })
                }
                break
            default:
                handleModalAction(action)
                break
        }
    }
}
</script>

<template>
    <div
        ref="extraDropdown"
        class="!ml-4">
        <VDropdown
            v-bind="dropdownProps"
            v-model:shown="open">
            <button
                type="button"
                :class="buttonClasses({ open })">
                <PhGear
                    class="size-7"
                    weight="duotone" />
            </button>

            <template #popper="{ hide }">
                <div class="w-[30rem]">
                    <header class="!p-4">
                        <h3 class="!my-0 font-sans text-base/normal font-medium tracking-[-2%]">
                            {{ $t(`nav.churchManagement`) }}
                        </h3>
                    </header>

                    <ul class="!my-2 flex flex-col gap-2">
                        <li
                            v-for="(link, index) in nav"
                            :key="`ChurchNav${index}`">
                            <TrinityNavIconLink
                                v-bind="link"
                                @action="action(hide, $event)" />
                        </li>
                    </ul>
                </div>
            </template>
        </VDropdown>
    </div>
    <ExtensionInstaller ref="extensionInstallModal" />
</template>

<style lang="scss">
.v-popper {
    &--some-open--main-nav-settings {
        @apply overflow-auto;
    }

    &--theme-main-nav-settings {
        &.v-popper__popper {
            @apply fixed;
        }

        .v-popper {
            &__wrapper {
                @apply overflow-hidden rounded-lg bg-white shadow-lg;
            }

            &__arrow-container {
                @apply absolute z-[1] m-0.5 size-3 border-[6px] border-solid border-transparent before:border before:shadow-lg md:block;
                @apply before:relative before:block before:size-3 before:rotate-45 before:border-solid before:border-transparent before:bg-white;
            }
        }

        &[data-popper-placement='bottom-end'] {
            .v-popper__arrow-container {
                @apply -bottom-1.5 left-[calc(50%-12px)] my-0 border-b-0 border-t-white before:-left-2 before:bottom-3;
            }
        }
    }
}
</style>
